import * as THREE from 'three';

/**
 * 二维有理 B-Spline 曲线 D0/D1/D2 求值器。
 *
 * 职责：使用齐次 De Boor 算法计算原生参数域内的曲线点，并通过齐次导数控制多边形
 * 精确计算一阶、二阶导数，为 Puppy CAD 内核 等价参数曲线适配器提供统一微分数据。
 */
export class RationalBSplineCurveEvaluator2D {
    /**
     * 计算二维有理 B-Spline 曲线点。
     * @param curve 二维有理 B-Spline 定义。
     * @param parameter 曲线原生参数。
     * @param target 结果写入目标。
     * @returns 写入后的二维曲线点。
     */
    public evaluatePoint(
        curve: RationalBSplineCurveDefinition2D,
        parameter: number,
        target: THREE.Vector2 = new THREE.Vector2(),
    ): THREE.Vector2 {
        const homogeneous = this.createHomogeneousControlPoints(curve);
        const value = this.evaluateHomogeneous(homogeneous, curve.degree, curve.knots, this.clampParameter(curve, parameter));
        if (Math.abs(value[2]!) <= Number.EPSILON) {
            return target.set(Number.NaN, Number.NaN);
        }
        return target.set(value[0]! / value[2]!, value[1]! / value[2]!);
    }

    /**
     * 计算二维有理 B-Spline 对原生参数的一阶导数。
     * @param curve 二维有理 B-Spline 定义。
     * @param parameter 曲线原生参数。
     * @param target 结果写入目标。
     * @returns 写入后的二维一阶导数。
     */
    public evaluateDerivative(
        curve: RationalBSplineCurveDefinition2D,
        parameter: number,
        target: THREE.Vector2 = new THREE.Vector2(),
    ): THREE.Vector2 {
        const homogeneous = this.createHomogeneousControlPoints(curve);
        const firstDefinition = this.deriveHomogeneousDefinition(homogeneous, curve.degree, curve.knots);
        const clampedParameter = this.clampParameter(curve, parameter);
        const value = this.evaluateHomogeneous(homogeneous, curve.degree, curve.knots, clampedParameter);
        const derivative = this.evaluateHomogeneous(
            firstDefinition.controlPoints,
            firstDefinition.degree,
            firstDefinition.knots,
            clampedParameter,
        );
        const denominator = value[2]! * value[2]!;
        if (Math.abs(denominator) <= Number.EPSILON) {
            return target.set(0, 0);
        }
        return target.set(
            (derivative[0]! * value[2]! - value[0]! * derivative[2]!) / denominator,
            (derivative[1]! * value[2]! - value[1]! * derivative[2]!) / denominator,
        );
    }

    /**
     * 计算二维有理 B-Spline 对原生参数的二阶导数。
     * @param curve 二维有理 B-Spline 定义。
     * @param parameter 曲线原生参数。
     * @param target 结果写入目标。
     * @returns 写入后的二维二阶导数。
     */
    public evaluateSecondDerivative(
        curve: RationalBSplineCurveDefinition2D,
        parameter: number,
        target: THREE.Vector2 = new THREE.Vector2(),
    ): THREE.Vector2 {
        if (curve.degree <= 1) {
            return target.set(0, 0);
        }
        const homogeneous = this.createHomogeneousControlPoints(curve);
        const firstDefinition = this.deriveHomogeneousDefinition(homogeneous, curve.degree, curve.knots);
        const secondDefinition = this.deriveHomogeneousDefinition(
            firstDefinition.controlPoints,
            firstDefinition.degree,
            firstDefinition.knots,
        );
        const clampedParameter = this.clampParameter(curve, parameter);
        const value = this.evaluateHomogeneous(homogeneous, curve.degree, curve.knots, clampedParameter);
        const firstDerivative = this.evaluateHomogeneous(
            firstDefinition.controlPoints,
            firstDefinition.degree,
            firstDefinition.knots,
            clampedParameter,
        );
        const secondDerivative = this.evaluateHomogeneous(
            secondDefinition.controlPoints,
            secondDefinition.degree,
            secondDefinition.knots,
            clampedParameter,
        );
        const weight = value[2]!;
        if (Math.abs(weight) <= Number.EPSILON) {
            return target.set(0, 0);
        }
        const weightSquared = weight * weight;
        const weightCubed = weightSquared * weight;
        return target.set(
            secondDerivative[0]! / weight
                - value[0]! * secondDerivative[2]! / weightSquared
                - 2 * firstDerivative[0]! * firstDerivative[2]! / weightSquared
                + 2 * value[0]! * firstDerivative[2]! * firstDerivative[2]! / weightCubed,
            secondDerivative[1]! / weight
                - value[1]! * secondDerivative[2]! / weightSquared
                - 2 * firstDerivative[1]! * firstDerivative[2]! / weightSquared
                + 2 * value[1]! * firstDerivative[2]! * firstDerivative[2]! / weightCubed,
        );
    }

    /**
     * 创建齐次控制点。
     * @param curve 有理 B-Spline 定义。
     * @returns `[x*w,y*w,w]` 控制点列表。
     */
    private createHomogeneousControlPoints(curve: RationalBSplineCurveDefinition2D): number[][] {
        return curve.controlPoints.map((point, index) => {
            const sourceWeight = curve.weights?.[index];
            const weight = Number.isFinite(sourceWeight) ? sourceWeight! : 1;
            return [point.x * weight, point.y * weight, weight];
        });
    }

    /**
     * 构造齐次 B-Spline 的一次导数定义。
     * @param controlPoints 当前齐次控制点。
     * @param degree 当前次数。
     * @param knots 当前展开节点向量。
     * @returns 导数控制点、次数和节点向量。
     */
    private deriveHomogeneousDefinition(
        controlPoints: readonly number[][],
        degree: number,
        knots: readonly number[],
    ): {controlPoints: number[][]; degree: number; knots: number[]} {
        if (degree <= 0 || controlPoints.length <= 1) {
            return {controlPoints: [[0, 0, 0]], degree: 0, knots: [knots[0] ?? 0, knots.at(-1) ?? 1]};
        }
        const derivativeControlPoints = controlPoints.slice(0, -1).map((point, index) => {
            const denominator = knots[index + degree + 1]! - knots[index + 1]!;
            const scale = Math.abs(denominator) <= Number.EPSILON ? 0 : degree / denominator;
            return point.map((value, axis) => scale * (controlPoints[index + 1]![axis]! - value));
        });
        return {controlPoints: derivativeControlPoints, degree: degree - 1, knots: knots.slice(1, -1)};
    }

    /**
     * 使用 De Boor 算法计算任意维度齐次 B-Spline 值。
     * @param controlPoints 齐次控制点。
     * @param degree B-Spline 次数。
     * @param knots 展开节点向量。
     * @param parameter 原生参数。
     * @returns 齐次坐标值。
     */
    private evaluateHomogeneous(
        controlPoints: readonly number[][],
        degree: number,
        knots: readonly number[],
        parameter: number,
    ): number[] {
        if (degree <= 0 || controlPoints.length === 1) {
            return [...controlPoints[0]!];
        }
        const lastControlPointIndex = controlPoints.length - 1;
        const resolvedDegree = Math.max(1, Math.min(Math.floor(degree), lastControlPointIndex));
        const span = this.findKnotSpan(lastControlPointIndex, resolvedDegree, parameter, knots);
        const working = Array.from({length: resolvedDegree + 1}, (_unused, index) => (
            [...controlPoints[span - resolvedDegree + index]!]
        ));
        for (let step = 1; step <= resolvedDegree; step += 1) {
            for (let index = resolvedDegree; index >= step; index -= 1) {
                const knotIndex = span - resolvedDegree + index;
                const denominator = knots[knotIndex + resolvedDegree - step + 1]! - knots[knotIndex]!;
                const alpha = Math.abs(denominator) <= Number.EPSILON
                    ? 0
                    : (parameter - knots[knotIndex]!) / denominator;
                for (let axis = 0; axis < working[index]!.length; axis += 1) {
                    working[index]![axis] = (1 - alpha) * working[index - 1]![axis]!
                        + alpha * working[index]![axis]!;
                }
            }
        }
        return working[resolvedDegree]!;
    }

    /**
     * 将参数限制到有效曲线域。
     * @param curve 曲线定义。
     * @param parameter 原始参数。
     * @returns 有效参数。
     */
    private clampParameter(curve: RationalBSplineCurveDefinition2D, parameter: number): number {
        return Math.max(curve.firstParameter, Math.min(curve.lastParameter, parameter));
    }

    /**
     * 查找参数所在节点区间。
     * @param lastControlPointIndex 控制点末索引。
     * @param degree 曲线次数。
     * @param parameter 曲线参数。
     * @param knots 完整节点向量。
     * @returns 节点区间索引。
     */
    private findKnotSpan(
        lastControlPointIndex: number,
        degree: number,
        parameter: number,
        knots: readonly number[],
    ): number {
        if (parameter >= knots[lastControlPointIndex + 1]!) return lastControlPointIndex;
        if (parameter <= knots[degree]!) return degree;
        let lowerIndex = degree;
        let upperIndex = lastControlPointIndex + 1;
        let middleIndex = Math.floor((lowerIndex + upperIndex) / 2);
        while (parameter < knots[middleIndex]! || parameter >= knots[middleIndex + 1]!) {
            if (parameter < knots[middleIndex]!) upperIndex = middleIndex;
            else lowerIndex = middleIndex;
            middleIndex = Math.floor((lowerIndex + upperIndex) / 2);
        }
        return middleIndex;
    }
}
